import { cloneElement, useCallback, useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Button, Dropdown, Header, Icon, Modal, Popup, type SemanticSIZES, Label as SLabel } from "semantic-ui-react"
import { asQuery } from "simplydo/core/api"
import { hasPermission } from "simplydo/core/permissions"
import type { OpenAPI } from "simplydo/interfaces"

import api from "api"
// @ts-ignore
import { useAppSelector } from "store"

type LabelStackViewProps = {
  labels: string[]
  horizontal?: boolean
  size?: SemanticSIZES
}

export const LabelStackView = ({ labels, horizontal, size = "medium" }: LabelStackViewProps) => {
  if (!labels || labels.length === 0) {
    return null
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        flexDirection: horizontal ? "row" : "column",
        gap: horizontal ? 2 : 4,
      }}
    >
      {labels?.map((label) => (
        <div key={label}>
          <LabelItem label={label} size={size} />
        </div>
      ))}
    </div>
  )
}

type LabelItemProps = {
  label: string
  onRemove?: (label: string) => void
  size?: SemanticSIZES
}

export const LabelItem = ({ label, onRemove, size = "tiny" }: LabelItemProps) => (
  // Default semantic padding makes the label too big for our use case
  // Default semantic color sets the label to .6 alpha which works for text but makes emojis look faded
  <SLabel size={size} style={{ padding: "0.2em 0.6em", color: "#5d5d5d" }}>
    {label}
    {onRemove && <Icon name="close" onClick={() => onRemove(label)} />}
  </SLabel>
)

type LabelChooserProps = {
  trigger?: React.ReactElement
  tooltip?: string
  isOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  disabled?: boolean
  challengeId?: string
  existing?: string[]
  onComplete: (label: string) => void
  onRemoveExisting: (label: string) => void
}

export const LabelChooser = ({
  isOpen,
  onOpen,
  onClose,
  trigger,
  tooltip,
  disabled,
  challengeId,
  existing,
  onComplete,
  onRemoveExisting,
}: LabelChooserProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<string | null>(null)

  const user = useAppSelector((state) => state.user)

  const canManageChallenges = hasPermission(user, "org.manageChallenges", user?.organisation)
  const canViewSettings = challengeId ? hasPermission(user, "challenge.editIdeaLabels", challengeId) : true

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = useCallback(() => {
    if (open && onClose) {
      onClose()
    }
    setOpen(false)
  }, [open, onClose])

  const openPopup = useCallback(() => {
    if (open) {
      return closePopup()
    }
    if (!open && onOpen) onOpen()
    setOpen(true)
  }, [open, onOpen, closePopup])

  const triggerElement = useMemo(() => {
    if (trigger) {
      return cloneElement(trigger, {
        onClick: disabled ? () => {} : openPopup,
      })
    }
    return null
  }, [trigger, openPopup, disabled])

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen)
    }
  }, [open, isOpen])

  const labelQuery = useQuery({
    queryKey: ["challenges", "labels"],
    queryFn: asQuery(api.challenges.getLabels),
    enabled: canManageChallenges || canViewSettings,
  })

  const chooseLabel = (label: string) => {
    if (existing?.includes(label)) {
      onRemoveExisting(label)

      api.queryClient.setQueryData(
        ["challenges", "labels"],
        (oldData: OpenAPI.GET<"/challenges/labels">["response"]) => {
          if (!oldData) {
            return { labels: [label], challengeLabels: [label] }
          }
          return {
            ...oldData,
            labels: oldData.labels.filter((l) => l !== label),
          }
        },
      )
    } else {
      onComplete(label)

      api.queryClient.setQueryData(
        ["challenges", "labels"],
        (oldData: OpenAPI.GET<"/challenges/labels">["response"]) => {
          if (!oldData) {
            return { labels: [label], challengeLabels: [label] }
          }
          return {
            ...oldData,
            labels: oldData.labels.includes(label) ? oldData.labels : [...oldData.labels, label],
          }
        },
      )
    }
  }

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={!!open}
        onClose={() => closePopup()}
      >
        <Modal.Header>Manage labels</Modal.Header>
        <Modal.Content>
          <p>
            Labels help categorise and filter {t("generic.ideas")} within {t("generic.challengeWithArticle")}. Labels
            are only visibile to managers with appropriate permissions.
          </p>
          {existing?.length > 0 ? (
            <div style={{ marginBottom: 5 }}>
              <Header as="h4" style={{ marginBottom: 5 }}>
                Selected labels
              </Header>
              {existing.map((label) => (
                <LabelItem key={label} label={label} onRemove={(label) => onRemoveExisting(label)} size="medium" />
              ))}
            </div>
          ) : null}
          <Header as="h4" style={{ marginBottom: 5 }}>
            Choose or add a label
          </Header>
          <p>Start typing to search existing labels or add a new one by typing it in full and pressing enter.</p>
          <Dropdown
            loading={labelQuery.isLoading}
            iconPosition="left"
            placeholder="Apply a label..."
            selection
            scrolling
            search
            allowAdditions
            selectOnBlur={false}
            selectOnNavigation={false}
            onChange={(e, { value }) => {
              chooseLabel(value as string)

              // Toggle label value to force Dropdown to reset its input field
              setValue(value as string)
              setValue(null)
            }}
            value={value}
            options={
              labelQuery.data?.labels?.map((label) => ({
                icon: existing?.includes(label) ? "check" : undefined,
                key: label,
                text: label,
                value: label,
              })) || []
            }
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => closePopup()}>Close</Button>
        </Modal.Actions>
      </Modal>
      <Popup on="hover" trigger={triggerElement} content={tooltip} disabled={!tooltip || disabled} />
    </>
  )
}
